<script setup lang='ts'>
import type { StoryblokRichTextNode } from '@storyblok/vue'
import { StoryblokRichText } from '@storyblok/vue'
import type { StoryblokAsset, StoryblokURL } from '../../types'

type FeaturedImageType = 'image' | 'lottie'

interface Props {
  blok: {
    _uid: string
    featured_image?: StoryblokAsset
    featured_image_src?: StoryblokURL
    featured_image_type: FeaturedImageType
    pretitle: string
    title: StoryblokRichTextNode
    description: StoryblokRichTextNode
  }
}
const { blok } = defineProps<Props>()

const featuredImageSrc = computed(() => {
  return blok.featured_image?.filename || blok.featured_image_src?.cached_url || blok.featured_image_src?.url
})
</script>

<template>
  <div
    v-editable="blok"
    flex="~ col md:row md:even:row-reverse"
    justify-between items-center
    w-full max-w="1080px" mx-auto
  >
    <ClientOnly v-if="blok.featured_image_type === 'lottie' && featuredImageSrc">
      <div
        w="md:50%"
        flex-shrink="0"
        class="rounded-[20px] overflow-hidden border-1 border-spGray2"
        :style="{
          backgroundColor: 'var(--background-color)',
        }"
      >
        <LottiePlayerDot :src="featuredImageSrc" />
      </div>
    </ClientOnly>
    <img v-else :src="featuredImageSrc" class="w-full md:w-50% rounded-[40px] object-cover">

    <aside flex="~ col" w="md:50%" justify-center class="py-[40px] gap-[24px] md:gap-[32px] md:py-[60px] md:px-[10px] items-start md:items-center">
      <div class="w-full max-w-[440px]">
        <div flex items-center>
          <div :class="$style.pretitle" class="font-circular-bold font-medium text-[16px] lg:text-[22.62px] leading-[28px] lg:leading-[24px] tracking-[-0.64px] lg:tracking-[-0.905px] mb-8">
            {{ blok.pretitle }}
          </div>
        </div>

        <div col class="gap-[24px] md:gap-[32px]">
          <StoryblokRichText :doc="blok.title" />

          <div class="text-[16px] leading-[28px] font-medium">
            <StoryblokRichText :doc="blok.description" />
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<style lang="scss" module>
.pretitle {
  color: var(--primary-color);
}
</style>
